import React, { useState, useEffect, useRef } from 'react';
import { 
  doc, 
  getDoc,
  Firestore 
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../hooks/AuthProvider';
import '../../styles/ConferenceAccess.css';
import * as XLSX from 'xlsx';

interface MetadataField {
  id: string;
  name: string;
  enabled?: boolean;
  accessCodeEnabled?: boolean; // New field for access code feature
  permissions?: {
    read: string | string[];
    write: string | string[];
    permittedReadEmails?: string[];
    permittedWriteEmails?: string[];
  };
}

interface MetadataCollection {
  permissions: {
    read: string | string[];
    write: string | string[];
  };
  fields: MetadataField[];
}

interface ConferenceAccessProps {
  db: Firestore;
  functions: Functions;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
}

// For Excel upload
interface ExcelUploadPreview {
  hasHeaders: boolean;
  headers: string[];
  data: any[][];
  selectedColumn: number;
}

// For access code management
interface AccessCode {
  conferenceId: string;
  code: string;
  enabled: boolean;
  lastUpdated: number;
}

// Constants for access types
const ALLOW_ALL = 'ALLOW_ALL';
const ALLOW_SPECIFIED = 'ALLOW_SPECIFIED';

const ConferenceAccess: React.FC<ConferenceAccessProps> = ({ 
  db, 
  functions, 
  onError, 
  onSuccess, 
  setLoading 
}) => {
  const { currentUser } = useAuth();
  const [conferences, setConferences] = useState<MetadataField[]>([]);
  const [selectedConference, setSelectedConference] = useState<string | null>(null);
  const [conferenceDetails, setConferenceDetails] = useState<MetadataField | null>(null);
  const [permittedUsers, setPermittedUsers] = useState<string[]>([]);
  const [newUserEmail, setNewUserEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [accessType, setAccessType] = useState<string>(ALLOW_ALL);
  
  // Access code state
  const [showAccessCodeSection, setShowAccessCodeSection] = useState<boolean>(false);
  const [accessCode, setAccessCode] = useState<string>('');
  const [isAccessCodeEnabled, setIsAccessCodeEnabled] = useState<boolean>(false);
  
  // Excel upload state
  const [showExcelUpload, setShowExcelUpload] = useState<boolean>(false);
  const [excelPreview, setExcelPreview] = useState<ExcelUploadPreview | null>(null);
  const [hasHeaders, setHasHeaders] = useState<boolean>(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cloud function references
  const manageSpecifiedUserFunc = httpsCallable(functions, 'manageSpecifiedUser');
  const updateConferenceAccessCodeFunc = httpsCallable(functions, 'updateConferenceAccessCode');
  
  // Load conferences from metadata
  useEffect(() => {
    const loadConferences = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      try {
        const metadataRef = doc(db, 'metadata', 'conferences');
        const metadataSnapshot = await getDoc(metadataRef);
        
        if (metadataSnapshot.exists()) {
          const metadataData = metadataSnapshot.data() as MetadataCollection;
          setConferences(metadataData.fields || []);
        } else {
          setConferences([]);
        }
      } catch (err) {
        console.error('Error loading conferences:', err);
        onError('Failed to load conferences. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadConferences();
  }, [db, currentUser, setLoading, onError]);

  // Load conference details and permissions when selected
  useEffect(() => {
    const loadConferenceDetails = async () => {
      if (!selectedConference) {
        setConferenceDetails(null);
        setPermittedUsers([]);
        setAccessType(ALLOW_ALL);
        setIsAccessCodeEnabled(false);
        setAccessCode('');
        return;
      }
      
      setLoading(true);
      try {
        const metadataRef = doc(db, 'metadata', 'conferences');
        const metadataSnapshot = await getDoc(metadataRef);
        
        if (metadataSnapshot.exists()) {
          const metadataData = metadataSnapshot.data() as MetadataCollection;
          
          // Find the selected conference in the fields array
          const conferenceField = metadataData.fields.find(field => field.id === selectedConference);
          
          if (conferenceField) {
            setConferenceDetails(conferenceField);
            
            // Determine access type
            if (conferenceField.permissions?.read) {
              const read = conferenceField.permissions.read;
              
              if (Array.isArray(read) && read.includes(ALLOW_SPECIFIED)) {
                setAccessType(ALLOW_SPECIFIED);
              } else if (read === ALLOW_SPECIFIED) {
                setAccessType(ALLOW_SPECIFIED);
              } else {
                setAccessType(ALLOW_ALL);
              }
            } else {
              setAccessType(ALLOW_ALL);
            }
            
            // Get permitted users
            if (conferenceField.permissions?.permittedReadEmails) {
              setPermittedUsers(conferenceField.permissions.permittedReadEmails);
            } else {
              setPermittedUsers([]);
            }
            
            // Get access code data directly from Firestore
            try {
              // Check if accessCodeEnabled is set in conference metadata
              const isFeatureEnabled = conferenceField.accessCodeEnabled || false;
              setIsAccessCodeEnabled(isFeatureEnabled);
              
              // Get access code document if feature is enabled
              if (isFeatureEnabled) {
                const accessCodeRef = doc(db, 'accessCodes', selectedConference);
                const accessCodeSnapshot = await getDoc(accessCodeRef);
                
                if (accessCodeSnapshot.exists()) {
                  const accessCodeData = accessCodeSnapshot.data() as AccessCode;
                  setAccessCode(accessCodeData.code || '');
                } else {
                  // No access code document yet, but don't generate a random one
                  // Just set an empty code
                  setAccessCode('');
                }
              }
            } catch (error) {
              console.error('Error loading access code:', error);
              // Not critical, so don't show error to user
            }
          } else {
            onError(`Conference with ID ${selectedConference} not found.`);
            setConferenceDetails(null);
            setPermittedUsers([]);
            setAccessType(ALLOW_ALL);
          }
        } else {
          onError('Conferences metadata not found.');
          setConferenceDetails(null);
          setPermittedUsers([]);
          setAccessType(ALLOW_ALL);
        }
      } catch (err) {
        console.error('Error loading conference details:', err);
        onError('Failed to load conference details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadConferenceDetails();
  }, [db, selectedConference, setLoading, onError]);

  // Handle access type change
  const handleAccessTypeChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newAccessType = e.target.value;
    
    if (newAccessType === accessType || !selectedConference) return;
    
    setLoading(true);
    
    try {
      // Call the cloud function to update access type
      const result = await manageSpecifiedUserFunc({
        collectionName: 'conferences',
        docId: selectedConference,
        operation: newAccessType === ALLOW_ALL ? 'SET_ALLOW_ALL' : 'SET_ALLOW_SPECIFIED'
      });
      
      const data: any = result.data;
      
      if (data.success) {
        // Update access type in state
        setAccessType(newAccessType);
        
        // Show success message
        onSuccess(`Access type updated to ${newAccessType === ALLOW_ALL ? 'allow all users' : 'allow specific users only'}.`);
        
        // Reload conference data
        const metadataRef = doc(db, 'metadata', 'conferences');
        const metadataSnapshot = await getDoc(metadataRef);
        
        if (metadataSnapshot.exists()) {
          const metadataData = metadataSnapshot.data() as MetadataCollection;
          const conferenceField = metadataData.fields.find(field => field.id === selectedConference);
          
          if (conferenceField) {
            setConferenceDetails(conferenceField);
            
            if (conferenceField.permissions?.permittedReadEmails) {
              setPermittedUsers(conferenceField.permissions.permittedReadEmails);
            } else {
              setPermittedUsers([]);
            }
          }
        }
      } else {
        onError(data.message || 'Failed to update access type.');
      }
    } catch (err: any) {
      console.error('Error updating access type:', err);
      onError(err.message || 'Failed to update access type. Please try again.');
      
      // Reset access type to original value
      setAccessType(accessType);
    } finally {
      setLoading(false);
    }
  };

  // Handle adding a new user
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    if (!newUserEmail.trim()) {
      setEmailError('Email is required');
      return;
    }
    
    if (!newUserEmail.includes('@')) {
      setEmailError('Invalid email format');
      return;
    }
    
    // Check if user already has permission
    if (permittedUsers.includes(newUserEmail.trim())) {
      setEmailError('User already has permission');
      return;
    }
    
    setEmailError('');
    setLoading(true);
    
    try {
      // Ensure we're in ALLOW_SPECIFIED mode before adding users
      if (accessType !== ALLOW_SPECIFIED && selectedConference) {
        // First change to ALLOW_SPECIFIED mode
        await manageSpecifiedUserFunc({
          collectionName: 'conferences',
          docId: selectedConference,
          operation: 'SET_ALLOW_SPECIFIED'
        });
        
        // Update access type in state
        setAccessType(ALLOW_SPECIFIED);
      }
      
      // Call the cloud function to add the user
      const result = await manageSpecifiedUserFunc({
        collectionName: 'conferences',
        docId: selectedConference!,
        email: newUserEmail.trim(),
        operation: 'ADD_READ'
      });
      
      const data: any = result.data;
      
      if (data.success) {
        // Update local state to include the new user
        setPermittedUsers(prev => [...prev, newUserEmail.trim()]);
        
        // Clear the input field
        setNewUserEmail('');
        
        // Show success message
        onSuccess(`User ${newUserEmail.trim()} now has read access to this conference.`);
      } else {
        onError(data.message || 'Failed to add user.');
      }
    } catch (err: any) {
      console.error('Error adding user:', err);
      onError(err.message || 'Failed to add user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle removing a user
  const handleRemoveUser = async (email: string) => {
    if (!window.confirm(`Are you sure you want to remove access for ${email}?`)) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Call the cloud function to remove the user
      const result = await manageSpecifiedUserFunc({
        collectionName: 'conferences',
        docId: selectedConference!,
        email: email,
        operation: 'REMOVE_READ'
      });
      
      const data: any = result.data;
      
      if (data.success) {
        // Update local state to remove the user
        setPermittedUsers(prev => prev.filter(e => e !== email));
        
        // Show success message
        onSuccess(`Access for ${email} has been removed.`);
      } else {
        onError(data.message || 'Failed to remove user.');
      }
    } catch (err: any) {
      console.error('Error removing user:', err);
      onError(err.message || 'Failed to remove user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle removing all users
  const handleRemoveAllUsers = async () => {
    if (!selectedConference || permittedUsers.length === 0) {
      return;
    }
    
    if (!window.confirm(`Are you sure you want to remove all ${permittedUsers.length} users? This action cannot be undone.`)) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Call the manageSpecifiedUser function with the entire array of emails
      const result = await manageSpecifiedUserFunc({
        collectionName: 'conferences',
        docId: selectedConference,
        email: [...permittedUsers], // Pass a copy of the entire array
        operation: 'REMOVE_READ'
      });
      
      const data: any = result.data;
      
      if (data.success) {
        // Store count for success message
        const userCount = permittedUsers.length;
        
        // Clear the local state
        setPermittedUsers([]);
        
        // Show success message
        onSuccess(`Removed all ${userCount} users from the conference access list.`);
      } else {
        onError(data.message || 'Failed to remove all users.');
      }
    } catch (err: any) {
      console.error('Error removing all users:', err);
      onError(err.message || 'Failed to remove all users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle back to conference list
  const handleBackToList = () => {
    setSelectedConference(null);
    setConferenceDetails(null);
    setPermittedUsers([]);
    setAccessType(ALLOW_ALL);
    setShowExcelUpload(false);
    setExcelPreview(null);
    setShowAccessCodeSection(false);
    setIsAccessCodeEnabled(false);
    setAccessCode('');
  };

// Handle Excel file upload with automatic email detection
const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
  const files = e.target.files;
  if (!files || files.length === 0) return;
  
  const file = files[0];
  const reader = new FileReader();
  
  reader.onload = (e) => {
    try {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });
      
      // Get first sheet
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // Parse data with header: 1 to always get arrays
      const parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
      
      if (parsedData.length === 0) {
        onError('The uploaded file appears to be empty.');
        return;
      }
      
      // Auto-detect email column and headers
      let emailColumnIndex = 0;
      let hasHeaders = true; // Default to assuming headers
      
      // Check if the first row contains an email address in any column
      const firstRow = parsedData[0];
      for (let i = 0; i < firstRow.length; i++) {
        const cell = firstRow[i];
        // If any cell in the first row contains '@', assume it's an email and there are no headers
        if (typeof cell === 'string' && cell.includes('@')) {
          emailColumnIndex = i;
          hasHeaders = false; // First row contains actual emails, so no headers
          break;
        }
      }
      
      // If we still think we have headers (no emails found in first row), check for email column headers
      if (hasHeaders) {
        let foundEmailColumn = false;
        for (let i = 0; i < firstRow.length; i++) {
          const cell = firstRow[i];
          // Check for common email header names
          if (typeof cell === 'string' && (
            cell.toLowerCase().includes('email') ||
            cell.toLowerCase().includes('e-mail') ||
            cell.toLowerCase().includes('mail')
          )) {
            emailColumnIndex = i;
            foundEmailColumn = true;
            break;
          }
        }
        
        // If no obvious email header found, check second row for emails
        if (!foundEmailColumn && parsedData.length > 1) {
          const secondRow = parsedData[1];
          for (let i = 0; i < secondRow.length; i++) {
            const cell = secondRow[i];
            if (typeof cell === 'string' && cell.includes('@')) {
              emailColumnIndex = i;
              break;
            }
          }
        }
      }
      
      // Generate headers based on whether we detected them
      let headers: string[];
      if (hasHeaders) {
        headers = firstRow.map((header, index) => {
          if (header && typeof header === 'string') {
            return header;
          } else {
            return `Untitled Column ${index + 1}`;
          }
        });
      } else {
        headers = firstRow.map((_, index) => `Untitled Column ${index + 1}`);
      }
      
      setHasHeaders(hasHeaders);
      
      // Set preview data
      setExcelPreview({
        hasHeaders,
        headers,
        data: parsedData,
        selectedColumn: emailColumnIndex
      });
      
    } catch (error) {
      console.error('Error reading Excel file:', error);
      onError('Failed to read Excel file. Please ensure it is a valid Excel spreadsheet.');
    }
  };
  
  reader.readAsArrayBuffer(file);
};

// Handle column selection
const handleColumnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
  if (!excelPreview) return;
  
  setExcelPreview({
    ...excelPreview,
    selectedColumn: parseInt(e.target.value)
  });
};

// Add emails from Excel file
const addEmailsFromExcel = async () => {
  if (!excelPreview || !selectedConference) return;
  
  setLoading(true);
  
  try {
    // Ensure we're in ALLOW_SPECIFIED mode before adding users
    if (accessType !== ALLOW_SPECIFIED) {
      // First change to ALLOW_SPECIFIED mode
      await manageSpecifiedUserFunc({
        collectionName: 'conferences',
        docId: selectedConference,
        operation: 'SET_ALLOW_SPECIFIED'
      });
      
      // Update access type in state
      setAccessType(ALLOW_SPECIFIED);
    }
    
    // Extract emails from the selected column
    const startRow = excelPreview.hasHeaders ? 1 : 0;
    const emails = excelPreview.data
      .slice(startRow)
      .map(row => row[excelPreview.selectedColumn])
      .filter(email => 
        email && 
        typeof email === 'string' && 
        email.includes('@') && 
        !permittedUsers.includes(email)
      );
    
    if (emails.length === 0) {
      onError('No valid email addresses found in the selected column.');
      setLoading(false);
      return;
    }
    
    // Call the cloud function to add the users
    const result = await manageSpecifiedUserFunc({
      collectionName: 'conferences',
      docId: selectedConference,
      email: emails,
      operation: 'ADD_READ'
    });
    
    const data: any = result.data;
    
    if (data.success) {
      // Update local state to include the new users
      setPermittedUsers(prev => [...prev, ...emails]);
      
      // Show success message
      onSuccess(`Added ${emails.length} email(s) to the conference access list.`);
      
      // Close the Excel upload view
      setShowExcelUpload(false);
      setExcelPreview(null);
      
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      onError(data.message || 'Failed to add users from Excel file.');
    }
  } catch (err: any) {
    console.error('Error adding users from Excel:', err);
    onError(err.message || 'Failed to add users from Excel file. Please try again.');
  } finally {
    setLoading(false);
  }
};

// Download current emails as Excel file
const downloadEmailsAsExcel = () => {
  if (permittedUsers.length === 0) {
    onError('No email addresses to download.');
    return;
  }
  
  try {
    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet([['Email Addresses'], ...permittedUsers.map(email => [email])]);
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Email Addresses');
    
    // Generate filename based on conference name
    const filename = `${conferenceDetails?.name || 'Conference'}_Access_List.xlsx`;
    
    // Write to file and trigger download
    XLSX.writeFile(wb, filename);
    
    onSuccess('Email addresses downloaded successfully.');
  } catch (err) {
    console.error('Error downloading emails as Excel:', err);
    onError('Failed to download email addresses. Please try again.');
  }
};


// Handle access code change
const handleAccessCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  setAccessCode(e.target.value);
};

// Save access code settings
const handleSaveAccessCode = async () => {
  if (!selectedConference) return;
  
  // Validate access code if enabled
  if (isAccessCodeEnabled && (!accessCode || accessCode.trim().length < 3)) {
    onError('Access code must be at least 3 characters.');
    return;
  }
  
  setLoading(true);
  
  try {
    // Call the cloud function to update access code settings
    const result = await updateConferenceAccessCodeFunc({
      conferenceId: selectedConference,
      enabled: isAccessCodeEnabled,
      code: isAccessCodeEnabled ? accessCode.trim() : undefined
    });
    
    const data: any = result.data;
    
    if (data.success) {
      // Update success message
      onSuccess(`Access code ${isAccessCodeEnabled ? 'enabled' : 'disabled'} for this conference.`);
      
      // Close the access code section
      setShowAccessCodeSection(false);
    } else {
      onError(data.message || 'Failed to update access code settings.');
    }
  } catch (err: any) {
    console.error('Error updating access code settings:', err);
    onError(err.message || 'Failed to update access code settings. Please try again.');
  } finally {
    setLoading(false);
  }
};

// Render the Excel upload view
const renderExcelUploadView = () => {
  if (!showExcelUpload) return null;
  
  return (
    <div className="excel-upload-modal">
      <div className="excel-upload-content">
        <div className="excel-upload-header">
          <h3>Upload Email Addresses</h3>
          <button 
            type="button" 
            className="close-btn"
            onClick={() => {
              setShowExcelUpload(false);
              setExcelPreview(null);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}
          >
            ✕
          </button>
        </div>
        
        <div className="excel-upload-body">
          {!excelPreview ? (
            // File upload form
            <div className="file-upload-form">
              <p>Upload an Excel file (.xlsx, .xls) containing email addresses.</p>
              <p className="help-text">We'll automatically detect email columns and determine if your file has headers.</p>
              
              <div className="form-field">
                <label htmlFor="excel-file">Select Excel File:</label>
                <input 
                  type="file" 
                  id="excel-file" 
                  accept=".xlsx, .xls" 
                  onChange={handleFileUpload}
                  ref={fileInputRef}
                />
              </div>
            </div>
          ) : (
            // Excel data preview
            <div className="excel-preview">
              <div className="preview-options">
                <div className="form-field">
                  <label htmlFor="column-select">Email Column:</label>
                  <select 
                    id="column-select" 
                    value={excelPreview.selectedColumn} 
                    onChange={handleColumnChange}
                  >
                    {excelPreview.headers.map((header, index) => (
                      <option key={index} value={index}>
                        {header || `Untitled Column ${index + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="detection-info">
                  {excelPreview.hasHeaders ? (
                    <span className="detection-badge headers-detected">Headers detected</span>
                  ) : (
                    <span className="detection-badge no-headers">No headers (data starts at row 1)</span>
                  )}
                </div>
              </div>
              
              <div className="unified-preview">
                <h4>Email Data Preview</h4>
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th className="row-header">Row</th>
                      <th className="selected-column">
                        {excelPreview.headers[excelPreview.selectedColumn] || `Untitled Column ${excelPreview.selectedColumn + 1}`}
                      </th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Filter and process rows to show only non-empty cells */}
                    {excelPreview.data
                      .slice(excelPreview.hasHeaders ? 1 : 0) // Start from appropriate row
                      .map((row, rowIndex) => {
                        const actualRowIndex = excelPreview.hasHeaders ? rowIndex + 1 : rowIndex;
                        const email = row[excelPreview.selectedColumn];
                        
                        // Skip empty or undefined values
                        if (!email) return null;
                        
                        const isValid = typeof email === 'string' && email.includes('@');
                        const isDuplicate = isValid && permittedUsers.includes(email);
                        
                        return (
                          <tr key={actualRowIndex}>
                            <td className="row-header">
                              {/* Show row number based on spreadsheet (1-indexed) */}
                              {excelPreview.hasHeaders ? actualRowIndex + 1 : actualRowIndex + 1}
                            </td>
                            <td className={`selected-column ${!isValid ? 'invalid-email' : ''} ${isDuplicate ? 'duplicate-email' : ''}`}>
                              {email}
                            </td>
                            <td className="status-cell">
                              {!isValid && <span className="status-badge invalid">Invalid email</span>}
                              {isDuplicate && <span className="status-badge duplicate">Already added</span>}
                              {isValid && !isDuplicate && <span className="status-badge valid">Valid</span>}
                            </td>
                          </tr>
                        );
                      })
                      // Filter out null rows (empty cells)
                      .filter(row => row !== null)
                      // Limit to first 10 rows for preview
                      .slice(0, 10)
                    }
                  </tbody>
                </table>
                
                {/* Get the total number of non-empty cells */}
                {(() => {
                  const nonEmptyCount = excelPreview.data
                    .slice(excelPreview.hasHeaders ? 1 : 0)
                    .filter(row => row[excelPreview.selectedColumn])
                    .length;
                  
                  if (nonEmptyCount > 10) {
                    return (
                      <p className="preview-note">
                        Showing 10 of {nonEmptyCount} non-empty rows.
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>
              
              <div className="email-summary">
                <div className="valid-email-count">
                  Valid new email addresses found: {
                    excelPreview.data
                      .slice(excelPreview.hasHeaders ? 1 : 0)
                      .filter(row => {
                        const email = row[excelPreview.selectedColumn];
                        return email && 
                              typeof email === 'string' && 
                              email.includes('@') && 
                              !permittedUsers.includes(email);
                      }).length
                  }
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="excel-upload-footer">
          {excelPreview ? (
            <>
              <button 
                type="button" 
                className="back-btn"
                onClick={() => {
                  setExcelPreview(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
              >
                Back
              </button>
              <button 
                type="button" 
                className="add-emails-btn"
                onClick={addEmailsFromExcel}
                disabled={accessType === ALLOW_ALL}
              >
                Add Email Addresses
              </button>
            </>
          ) : (
            <button 
              type="button" 
              className="cancel-btn"
              onClick={() => setShowExcelUpload(false)}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Render the access code configuration view
const renderAccessCodeSection = () => {
  if (!showAccessCodeSection) return null;
  
  return (
    <div className="access-code-modal">
      <div className="access-code-content">
        <div className="access-code-header">
          <h3>Access Code Configuration</h3>
          <button 
            type="button" 
            className="close-btn"
            onClick={() => setShowAccessCodeSection(false)}
          >
            ✕
          </button>
        </div>
        
        <div className="access-code-body">
          <div className="access-code-form">
            <div className="form-field toggle-field">
              <label htmlFor="access-code-toggle">Enable Access Code:</label>
              <button 
                type="button"
                className={`toggle-button ${isAccessCodeEnabled ? 'toggle-on' : 'toggle-off'}`}
                onClick={() => {
                  // Simply toggle the state without generating a random code
                  setIsAccessCodeEnabled(!isAccessCodeEnabled);
                }}
                style={{ display: 'inline-block', marginLeft: '10px' }}
              >
                {isAccessCodeEnabled ? 'On' : 'Off'}
              </button>
            </div>
              
            <div className="form-field">
              <label htmlFor="access-code-input">Access Code:</label>
              <input 
                type="text" 
                id="access-code-input" 
                value={accessCode}
                onChange={handleAccessCodeChange}
                disabled={!isAccessCodeEnabled}
                placeholder="Enter access code"
                className={!isAccessCodeEnabled ? 'disabled-input' : ''}
              />
              {isAccessCodeEnabled && (
                <div className="form-field-hint">
                  Users will enter this code to gain access to the conference.
                </div>
              )}
            </div>
            
            <div className="access-code-instructions">
              {isAccessCodeEnabled ? (
                <div className="info-message">
                  <h4>How it works:</h4>
                  <ol>
                    <li>Users must be logged in to access the conference.</li>
                    <li>When they attempt to access a restricted conference, they'll be prompted for an access code.</li>
                    <li>After entering the correct code, their email will be automatically added to the access list.</li>
                  </ol>
                </div>
              ) : (
                <div className="disabled-message">
                  <p>Access code feature is currently disabled.</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="access-code-footer">
          <button 
            type="button" 
            className="cancel-btn"
            onClick={() => setShowAccessCodeSection(false)}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="save-btn"
            onClick={handleSaveAccessCode}
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

// Render the conference list view
const renderConferenceList = () => (
  <div className="conference-access-list-view">
    <div className="view-header">
      <h2>Access Management</h2>
    </div>
    
    <div className="conference-access-instructions">
      <p>Select a conference to manage user access permissions.</p>
    </div>
    
    <div className="conference-cards">
      {conferences.length > 0 ? (
        conferences.map(conference => (
          <div
            key={conference.id}
            className="conference-card"
            onClick={() => setSelectedConference(conference.id)}
          >
            <div className="card-content">
              <h3 className="card-title">{conference.name}</h3>
              <div className="card-status">
                <span className={`status-indicator ${conference.enabled ? 'enabled' : 'disabled'}`}>
                  {conference.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="card-footer">
                <button 
                  type="button"
                  className="manage-btn" 
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent event bubbling
                    setSelectedConference(conference.id);
                  }}
                >
                  Manage Access
                </button>
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="empty-state">
          <p>No conferences available. Create a conference first in the Conference Manager.</p>
        </div>
      )}
    </div>
  </div>
);

// Render the permissions detail view
const renderPermissionsDetail = () => (
  <div className="conference-access-detail-view">
    <div className="view-header">
      <button 
        type="button"
        className="back-btn" 
        onClick={handleBackToList}
      >
        ← Back to Conferences
      </button>
      <h2>Manage Access: {conferenceDetails?.name}</h2>
    </div>
    
    <div className="access-type-selector">
      <label htmlFor="access-type">Access Control:</label>
      <select 
        id="access-type" 
        value={accessType} 
        onChange={handleAccessTypeChange}
        className="access-type-select"
      >
        <option value={ALLOW_ALL}>Allow All Users</option>
        <option value={ALLOW_SPECIFIED}>Allow Specific Users Only</option>
      </select>
      
      {accessType === ALLOW_SPECIFIED && (
        <button
          type="button"
          className="access-code-btn"
          onClick={() => setShowAccessCodeSection(true)}
        >
          {isAccessCodeEnabled ? 'Access Code Enabled' : 'Configure Access Code'}
        </button>
      )}
    </div>
    
    <div className="permissions-container">
      <div className="add-user-section">
        <h3>Add User Access</h3>
        <form onSubmit={handleAddUser} className="add-user-form">
          <div className="form-field">
            <label>User Email</label>
            <input
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              placeholder="Enter user email"
              className={emailError ? 'input-error' : ''}
              disabled={accessType === ALLOW_ALL}
            />
            {emailError && <div className="error-message">{emailError}</div>}
            {accessType === ALLOW_ALL && (
              <div className="info-message">
                Switch to "Allow Specific Users Only" to manage individual access.
              </div>
            )}
          </div>
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="add-user-btn"
              disabled={!newUserEmail.trim() || accessType === ALLOW_ALL}
            >
              Grant Access
            </button>
            
            <button 
              type="button" 
              className="upload-excel-btn"
              onClick={() => setShowExcelUpload(true)}
              disabled={accessType === ALLOW_ALL}
            >
              Upload from Excel
            </button>
          </div>
        </form>
      </div>
      
      <div className="current-users-section">
        <div className="current-users-header">
          <h3>Current Users with Access</h3>
          
          {accessType !== ALLOW_ALL && permittedUsers.length > 0 && (
            <div className="user-actions">
              <button 
                type="button" 
                className="download-excel-btn"
                onClick={downloadEmailsAsExcel}
                title="Download all email addresses as Excel file"
              >
                Download
              </button>
              
              <button 
                type="button" 
                className="remove-all-btn"
                onClick={handleRemoveAllUsers}
                title="Remove all users from access list"
              >
                Remove All
              </button>
            </div>
          )}
        </div>
        
        {accessType === ALLOW_ALL ? (
          <div className="public-access-message">
            <p>This conference is publicly accessible to all users.</p>
          </div>
        ) : permittedUsers.length > 0 ? (
          <div className="users-list-container">
            <div className="users-count">
              <p>{permittedUsers.length} user(s) with access</p>
            </div>
            
            <ul className="users-list">
              {permittedUsers.map(email => (
                <li key={email} className="user-item">
                  <span className="user-email">{email}</span>
                  <button
                    type="button"
                    className="remove-btn"
                    onClick={() => handleRemoveUser(email)}
                    aria-label={`Remove access for ${email}`}
                  >
                    <span className="icon-delete">🗑️</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="empty-users-message">
            <p>No users have been granted access to this conference.</p>
            
            {/* Show access code status if enabled */}
            {isAccessCodeEnabled ? (
              <div className="access-code-status">
                <p className="info-message">
                  Access code is enabled. Users can gain access by entering the code.
                </p>
              </div>
            ) : (
              <p className="warning-message">
                Warning: With current settings, no one can access this conference.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);

return (
  <div className="conference-access">
    {selectedConference && conferenceDetails ? renderPermissionsDetail() : renderConferenceList()}
    {renderExcelUploadView()}
    {renderAccessCodeSection()}
  </div>
);
};

export default ConferenceAccess;